import {Component, EventEmitter, HostListener, inject, Input, Output, ViewChild} from '@angular/core';
import {DxDataGridComponent} from "devextreme-angular";
import {BaseComponent} from "../../../../BaseComponent";
import {RowClickEvent} from "devextreme/ui/data_grid";
import {Gateway} from "../../../../../models/gateway/Gateway";
import {SendGatewayDevicesConfigCB} from "../../../../../ope/gateway/SendGatewayDevicesConfigCB";
import {DismissGatewayCB} from "../../../../../ope/gateway/DismissGatewayCB";
import {ReplaceGatewayCB} from "../../../../../ope/gateway/ReplaceGatewayCB";
import {GetFlashedGatewaysCB} from "../../../../../ope/gateway/GetFlashedGatewaysCB";
import {GatewayOpeService} from "../../../../../services/ope/gatewayOpe.service";
import {AssignGatewayCB} from "../../../../../ope/gateway/AssignGatewayCB";
import {GatewayStatusEnum} from "../../../../../models/gateway/GatewayStatusEnum";
import {
  RevertGatewayFieldsPendingConfigCB
} from "../../../../../ope/gateway-pending-config/RevertGatewayFieldsPendingConfigCB";
import {GatewayPendingConfigOpeService} from "../../../../../services/ope/gatewayPendingConfigOpe.service";
import {ChangeTypeEnum} from "../../../../../ope/gateway-pending-config/GatewayPendingConfigPayloadDTO";

@Component({
  selector: 'app-gateway-table',
  templateUrl: './gateway-table.component.html',
  styleUrls: ['./gateway-table.component.css', '../../gateway-device.component.css']
})
export class GatewayTableComponent extends BaseComponent {

  @Input() selectedPartitionId?: number;
  @Input() gateways: Gateway[];
  /**
   * Id of the selected gateway in the gateway data grid (selected by clicking on a row)
   */
  @Input() selectedRowGateway: any;

  /**
   * When true (device drawer open) only the essential columns are shown
   */
  @Input() compactMode: boolean = false;

  /**
   * Columns hidden in compact mode. Add/remove keys here to change what is hidden;
   * the keys are the ones passed to isColumnVisible() in the template.
   */
  protected readonly COMPACT_HIDDEN_COLUMNS: string[] = ['macAddress', 'changeType', 'actions'];

  @ViewChild('gatewayGrid') gatewayGrid?: DxDataGridComponent;

  @Output() onGatewayRowClick: EventEmitter<RowClickEvent> = new EventEmitter<RowClickEvent>();
  @Output() updateGatewaysRequest: EventEmitter<any> = new EventEmitter<any>();
  @Output() showNewGatewayForm: EventEmitter<any> = new EventEmitter<any>();
  @Output() openEditGatewayDrawer: EventEmitter<Gateway> = new EventEmitter<Gateway>();

  gatewayService: GatewayOpeService = inject(GatewayOpeService);
  gatewayPendingConfigService: GatewayPendingConfigOpeService = inject(GatewayPendingConfigOpeService);

  protected readonly GatewayStatusEnum = GatewayStatusEnum;
  protected readonly ChangeTypeEnum = ChangeTypeEnum;

  get hasUpdatedGateways(): boolean {
    return !!this.gateways?.some(g => g.changeType === ChangeTypeEnum.UPDATED);
  }
  /**
   * Selected gateway to dismiss
   */
  gatewayToDismiss?: Gateway;
  /**
   * Show the confirm dialog to dismiss a gateway when true
   */
  dismissGatewayForm: boolean = false;
  /**
   * Show replace gateway form when true
   */
  openReplaceGatewayForm: boolean = false;
  /**
   * Real gateway to be replaced
   */
  gatewayReplaced?: Gateway;
  /**
   * Replacing gateway
   */
  selectedGatewayReplacing?: Gateway;
  /**
   * Show assign gateway form when true
   */
  openAssignGatewayForm: boolean = false;
  /**
   * Fake gateway to assign
   */
  gatewayToAssign?: Gateway;
  /**
   * Real gateway to assign
   */
  selectedGatewayToAssign?: Gateway;
  /**
   * Filtered gateways list
   */
  gatewaysFiltered?: Gateway[];

  constructor() {
    super();
    this.gateways = [];
  }

  isColumnVisible(key: string): boolean {
    return !(this.compactMode && this.COMPACT_HIDDEN_COLUMNS.includes(key));
  }

  /**
   * The grid only re-measures its columns on window resize: do it once the host width transition
   * is over, otherwise it keeps the old column widths and overflows (horizontal scroll).
   */
  @HostListener('transitionend', ['$event'])
  onHostTransitionEnd(event: TransitionEvent) {
    if (event.target === event.currentTarget && event.propertyName === 'width')
      this.gatewayGrid?.instance?.updateDimensions();
  }

  parseGatewayStatus = (data: any) => {
    let status = `REGISTRY.GATEWAY.STATES.${data.status}`;
    return this.translate.instant(status);
  };


  /**
   * Revert the pending changes on the gateway own fields (name/type/sending frequency),
   * leaving the devices already drafted untouched.
   */
  revertGatewayFieldsPendingConfig($event: any, data: Gateway) {
    $event.stopPropagation();
    this.sendRevertGatewayFieldsCommand(data.id);
  }

  /**
   * Show assign gateway form
   */
  startReplaceGateway($event: any, data: Gateway) {
    $event.stopPropagation();
    this.showReplaceGatewayForm(data);
  }

  /**
   * Show assign gateway form
   */
  startAssignGateway($event: any, data: Gateway) {
    $event.stopPropagation();
    this.showAssignGatewayForm(data);
  }

  /**
   * Call uploadConfig ope
   */
  uploadConfig($event: any, data: Gateway) {
    $event.stopPropagation();
    this.sendGatewayDevicesConfigCommand(data.id);
  }

  /**
   * Show dismiss gateway form
   */
  showDismissGatewayForm($event: any, data: Gateway) {
    $event.stopPropagation();
    this.gatewayToDismiss = data;
    this.dismissGatewayForm = true;
  }

  /**
   * Open assign gateway form
   */
  showReplaceGatewayForm(gateway: Gateway) {
    this.openReplaceGatewayForm = true;
    this.getFlashedGatewaysToAssign();
    this.gatewayReplaced = gateway;
  }

  /**
   * Open assign gateway form
   */
  showAssignGatewayForm(gateway: Gateway) {
    this.openAssignGatewayForm = true;
    this.getFlashedGatewaysToAssign();
    this.gatewayToAssign = gateway;
  }

  /**
   * Call RevertGatewayFieldsPendingConfigOPE
   */
  sendRevertGatewayFieldsCommand(gatewayId: number) {
    const cb: RevertGatewayFieldsPendingConfigCB = new RevertGatewayFieldsPendingConfigCB();
    cb.i.gatewayId = gatewayId;

    this.gatewayPendingConfigService.revertGatewayFieldsPendingConfig(cb).then((_) => {

      this.toastService.openSuccessToastMessage(this.translate.instant("REGISTRY.GATEWAY.TEXT.UPDATED"));

      this.getGateways();

    }).catch((_) => {

      this.getGateways();
    });
  }

  /**
   * Call SendGatewayDeviceConfigOPE
   */
  sendGatewayDevicesConfigCommand(gatewayId: number) {
    const cb = new SendGatewayDevicesConfigCB();
    cb.i.id = gatewayId;

    this.gatewayService.sendGatewayDevicesConfig(cb).then((_) => {

      this.toastService.openSuccessToastMessage(this.translate.instant("REGISTRY.GATEWAY.TEXT.UPDATED"));

      this.getGateways();

    }).catch((_) => {

      this.getGateways();
    });

  }


  /**
   * Ope to dismiss a gateway
   */
  dismissGateway() {

    // close dismiss gateway dialog
    this.dismissGatewayForm = false;

    // create cb
    const cb: DismissGatewayCB = new DismissGatewayCB();
    cb.i.id = this.gatewayToDismiss!.id;

    // call ope
    this.gatewayService.dismissGateway(cb).then((_) => {
      this.toastService.openSuccessToastMessage(this.translate.instant("REGISTRY.GATEWAY.TEXT.DISMISSED"));
      this.getGateways();
      this.gatewayToDismiss = undefined;
    });
  }

  /**
   * Close dismiss gateway form
   */
  closeDismissGatewayForm() {
    this.gatewayToDismiss = undefined;
    this.dismissGatewayForm = false;
  }


  /**
   * Close assign gateway form
   */
  closeReplaceGatewayForm() {
    this.openReplaceGatewayForm = false;
    this.gatewayReplaced = undefined;
    this.selectedGatewayReplacing = undefined;
  }

  /**
   * Get id of selected real gateway to assign
   */
  onGatewayToReplaceRowClick($event: any) {
    this.selectedGatewayReplacing = $event.data;
  }

  /**
   * Call replaceGateway ope
   */
  replaceGateway() {

    // create cb
    const cb: ReplaceGatewayCB = new ReplaceGatewayCB();
    cb.i.id = this.gatewayReplaced!.id;
    cb.i.replacingId = this.selectedGatewayReplacing!.id;

    // call ope
    this.gatewayService.replaceGateway(cb).then((_) => {
      this.openReplaceGatewayForm = false;
      this.gatewayReplaced = undefined;
      this.selectedGatewayReplacing = undefined;
      this.toastService.openSuccessToastMessage(this.translate.instant("REGISTRY.GATEWAY.TEXT.REPLACED"));
      this.getGateways();
    });
  }

  /**
   * Check if both fake and real gateways are selected
   */
  checkIfBothGatewaysReplaced() {
    return !!this.gatewayReplaced && !!this.selectedGatewayReplacing;
  }

  getFlashedGatewaysToAssign() {
    const cb: GetFlashedGatewaysCB = new GetFlashedGatewaysCB();

    this.gatewayService.getFlashedGateways(cb).then((_) => {
      this.gatewaysFiltered = cb.o.gateways;
    });
  }


  /**
   * Close assign gateway form
   */
  closeAssignGatewayForm() {
    this.openAssignGatewayForm = false;
    this.gatewayToAssign = undefined;
    this.selectedGatewayToAssign = undefined;
  }

  /**
   * Get id of selected real gateway to assign
   */
  onGatewayToAssignRowClick($event: any) {
    this.selectedGatewayToAssign = $event.data;
  }

  /**
   * Call assignGateway ope
   */
  assignGateway() {

    // create cb
    const cb: AssignGatewayCB = new AssignGatewayCB();
    cb.i.id = this.gatewayToAssign!.id;
    cb.i.macAddressGatewayId = this.selectedGatewayToAssign!.id;

    // call ope
    this.gatewayService.assignGateway(cb).then((_) => {
      this.openAssignGatewayForm = false;
      this.gatewayToAssign = undefined;
      this.selectedGatewayToAssign = undefined;
      this.toastService.openSuccessToastMessage(this.translate.instant("REGISTRY.GATEWAY.TEXT.ASSIGNED"));
      this.getGateways();
    });
  }

  /**
   * Check if both fake and real gateways are selected
   */
  checkIfBothGatewaysAssigned() {
    return !!this.gatewayToAssign && !!this.selectedGatewayToAssign;
  }

  getGateways() {
    this.updateGatewaysRequest.emit();
  }

}
